import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  CircleAlert,
  Lightbulb,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import type { KeyboardEvent } from 'react';
import { formatDateBR, type FortnightlyReport } from '@lira/shared';
import { apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { MoneyText, Skeleton } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePrivacyStore } from '@/stores/privacy';

const metricTone = {
  income: 'bg-income-weak text-income-text',
  expense: 'bg-expense-weak text-expense-text',
  neutral: 'bg-surface text-ink',
} as const;

function scrollListByKeyboard(event: KeyboardEvent<HTMLUListElement>) {
  const list = event.currentTarget;
  const movement: Partial<Record<string, number>> = {
    ArrowDown: 40,
    ArrowUp: -40,
    PageDown: list.clientHeight,
    PageUp: -list.clientHeight,
  };
  if (event.key === 'Home' || event.key === 'End') {
    event.preventDefault();
    list.scrollTop = event.key === 'Home' ? 0 : list.scrollHeight;
    return;
  }
  const delta = movement[event.key];
  if (delta !== undefined) {
    event.preventDefault();
    list.scrollBy({ top: delta });
  }
}

function MetricCard({
  label,
  cents,
  tone = 'neutral',
  colorBySign = false,
}: {
  label: string;
  cents: number;
  tone?: keyof typeof metricTone;
  colorBySign?: boolean;
}) {
  return (
    <div className={`min-w-0 rounded-[var(--radius-card)] p-5 shadow-card ${metricTone[tone]}`}>
      <p className="text-xs font-bold uppercase tracking-[0.14em] opacity-75">{label}</p>
      <MoneyText
        cents={cents}
        colorBySign={colorBySign}
        showPositiveSign={colorBySign}
        bold
        className={`mt-2 block text-2xl tabular-nums sm:text-3xl ${tone === 'income' ? 'text-income-text' : tone === 'expense' ? 'text-expense-text' : ''}`}
      />
    </div>
  );
}

const insightStyle = {
  positive: { icon: Sparkles, className: 'border-income/30 bg-income-weak', iconClass: 'text-income-text' },
  warning: { icon: CircleAlert, className: 'border-expense/30 bg-expense-weak', iconClass: 'text-expense-text' },
  opportunity: { icon: Lightbulb, className: 'border-primary/30 bg-primary-weak', iconClass: 'text-primary' },
} as const;

export function FortnightlyReportPage() {
  const { reportId = '' } = useParams();
  const hidden = usePrivacyStore((state) => state.hidden);
  const query = useQuery({
    queryKey: ['fortnightly-report', reportId],
    queryFn: () => apiGet<{ report: FortnightlyReport }>(`/fortnightly-reports/${reportId}`),
    enabled: Boolean(reportId),
  });

  if (query.isLoading) return <Skeleton variant="card" className="h-96" />;
  if (query.isError || !query.data) {
    return (
      <section className="space-y-5">
        <PageHeader title="Resumo financeiro" />
        <div className="rounded-[var(--radius-card)] bg-surface p-6 text-sm text-expense shadow-card">
          {query.isError ? errorMessage(query.error) : 'Resumo não encontrado.'}
        </div>
      </section>
    );
  }

  const report = query.data.report;
  const comparison = report.expenseChangeBasisPoints;
  const ComparisonIcon = comparison !== null && comparison <= 0 ? TrendingDown : TrendingUp;
  const periodLabel = report.reportKind === 'month'
    ? 'Fechamento mensal completo'
    : report.reportKind === 'legacy_second_half'
      ? 'Snapshot anterior · segunda quinzena'
      : 'Primeira quinzena';

  return (
    <section className="min-w-0 space-y-5">
      <Link to="/relatorios" className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-primary hover:underline">
        <ArrowLeft className="size-4" /> Voltar aos relatórios
      </Link>
      <PageHeader
        title={hidden ? 'Resumo financeiro' : report.title}
        subtitle={`${periodLabel} · ${formatDateBR(report.periodStart)} a ${formatDateBR(report.periodEnd)}`}
        meta={<CalendarDays className="size-5 text-muted" aria-hidden />}
      />

      <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Ganhos realizados" cents={report.incomeCents} tone="income" />
        <MetricCard label="Despesas realizadas" cents={report.expenseCents} tone="expense" />
        <MetricCard label="Saldo realizado" cents={report.balanceCents} colorBySign />
        <MetricCard label="Compromissos a pagar" cents={report.pendingExpenseCents} tone="expense" />
      </div>

      <article className="min-w-0 rounded-[var(--radius-panel)] bg-surface p-5 shadow-card sm:p-6">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Análise do Sam</p>
        <h2 className="mt-2 text-xl font-bold text-ink">Leitura financeira do período</h2>
        {hidden ? (
          <p className="mt-3 text-sm text-muted" aria-label="Valor oculto">Resumo oculto enquanto a privacidade de valores estiver ativa.</p>
        ) : (
          <p className="mt-3 max-w-3xl whitespace-pre-line break-words text-[15px] leading-7 text-ink">{report.summary}</p>
        )}

        {!hidden && comparison !== null && (
          <div className="mt-5 flex items-start gap-3 rounded-xl bg-canvas p-4">
            <ComparisonIcon className={`mt-0.5 size-5 shrink-0 ${comparison <= 0 ? 'text-income-text' : 'text-expense-text'}`} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">Comparação com o mesmo período do mês anterior</p>
              <p className="mt-1 text-sm text-muted">
                As despesas realizadas {comparison === 0 ? 'ficaram estáveis' : comparison < 0 ? 'caíram' : 'subiram'}{' '}
                {Math.abs(comparison / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%.
              </p>
            </div>
          </div>
        )}
      </article>

      <div className="grid min-w-0 gap-5 lg:grid-cols-2">
        <section className="min-w-0 rounded-[var(--radius-card)] bg-surface p-5 shadow-card">
          <h2 className="text-lg font-bold text-ink">Categorias e evolução</h2>
          {hidden ? (
            <p className="mt-4 text-sm text-muted" aria-label="Valor oculto">Categorias ocultas enquanto a privacidade de valores estiver ativa.</p>
          ) : report.topCategories.length === 0 ? (
            <p className="mt-4 text-sm text-muted">Nenhuma despesa realizada neste período.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {report.topCategories.map((category) => (
                <li key={category.categoryId} className="flex min-w-0 items-center gap-3 py-3">
                  <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: category.color }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">{category.name}</p>
                    <p className={`mt-0.5 text-xs ${category.changeBasisPoints !== null && category.changeBasisPoints > 0 ? 'text-expense-text' : 'text-income-text'}`}>
                      {category.changeBasisPoints === null
                        ? 'Sem base comparável'
                        : `${category.changeBasisPoints > 0 ? '↑' : category.changeBasisPoints < 0 ? '↓' : '→'} ${Math.abs(category.changeBasisPoints / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% vs. mês anterior`}
                    </p>
                  </div>
                  <MoneyText cents={category.totalCents} bold className="shrink-0 text-sm text-expense-text" />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="min-w-0 rounded-[var(--radius-card)] bg-surface p-5 shadow-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-ink">Compromissos pendentes</h2>
              <p className="mt-1 text-xs text-muted">Lista do período, ordenada por vencimento</p>
            </div>
            {!hidden && <span className="rounded-full bg-expense-weak px-2.5 py-1 text-xs font-bold text-expense-text">{report.pendingCommitments.length}</span>}
          </div>
          {hidden ? (
            <p className="mt-4 text-sm text-muted" aria-label="Valor oculto">Compromissos ocultos enquanto a privacidade de valores estiver ativa.</p>
          ) : report.pendingCommitments.length === 0 ? (
            <p className="mt-4 rounded-xl bg-canvas p-4 text-sm text-muted">Nenhum compromisso pendente neste período.</p>
          ) : (
            <ul
              data-testid="pending-commitments-scroll"
              tabIndex={0}
              role="region"
              aria-label="Compromissos pendentes com rolagem"
              onKeyDown={scrollListByKeyboard}
              className="mt-4 max-h-56 space-y-2 overflow-y-auto overscroll-contain pr-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {report.pendingCommitments.map((commitment) => (
                <li key={commitment.transactionId} className="flex min-w-0 items-center gap-3 rounded-xl bg-canvas p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">{commitment.description}</p>
                    <p className="mt-1 text-xs text-muted">{formatDateBR(commitment.date)} · {commitment.categoryName}</p>
                  </div>
                  <MoneyText
                    cents={commitment.amountCents}
                    bold
                    className={`shrink-0 text-sm ${commitment.type === 'expense' ? 'text-expense-text' : 'text-income-text'}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="min-w-0 rounded-[var(--radius-panel)] bg-surface p-5 shadow-card sm:p-6">
        <div className="flex items-center gap-2">
          <Lightbulb className="size-5 text-primary" aria-hidden />
          <h2 className="text-xl font-bold text-ink">Insights para melhorar</h2>
        </div>
        {hidden ? (
          <p className="mt-4 text-sm text-muted" aria-label="Valor oculto">Insights ocultos enquanto a privacidade de valores estiver ativa.</p>
        ) : report.insights.length === 0 ? (
          <p className="mt-4 text-sm text-muted">Ainda não há histórico suficiente para gerar insights comparativos.</p>
        ) : (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {report.insights.map((insight) => {
              const style = insightStyle[insight.kind];
              const Icon = style.icon;
              return (
                <article key={`${insight.kind}-${insight.title}`} className={`flex items-start gap-3 rounded-xl border p-4 ${style.className}`}>
                  <Icon className={`mt-0.5 size-5 shrink-0 ${style.iconClass}`} />
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-ink">{insight.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-muted">{insight.body}</p>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </section>
  );
}
